import React from 'react';
import { useShop } from '../context/ShopContext';
import { 
  MapPin, 
  Phone, 
  Clock, 
  Navigation, 
  ShieldCheck, 
  Sparkles, 
  Car, 
  Award,
  MessageCircle,
  Mail
} from 'lucide-react';

export default function StoreLocatorSection() {
  const { storeSettings, marketingOfferData } = useShop();

  if (storeSettings?.storeLocatorEnabled === false) return null;

  const storeName = storeSettings?.storeName || 'M.L. Jewellers & Kitchenware';
  const storeAddress = storeSettings?.storeAddress || storeSettings?.address || 'Main Market Road, Near Clock Tower, Jaipur, Rajasthan - 302001';
  const storePhone = storeSettings?.storePhone || storeSettings?.phone || '+91 98100 23456';
  const storeEmail = storeSettings?.storeEmail || storeSettings?.email || 'contact@mljewellers.com';
  const storeTimings = storeSettings?.storeTimings || 'Mon - Sun: 10:30 AM to 08:30 PM (All 7 Days Open)';
  const cleanPhone = storePhone.replace(/[^0-9+]/g, '');

  const badgeText = storeSettings?.storeLocatorBadge || 'Visit Our Flagship Vault Showroom';
  const headingText = storeSettings?.storeLocatorTitle || 'Store Location & Showroom Experience';
  const subtitleText = storeSettings?.storeLocatorSubtitle || 'Step into our regal showroom to view hand-crafted bridal sets, get certified Karatmeter testing, and trade bullion at live market rates.';

  const amenity1 = storeSettings?.storeAmenity1 || 'BIS Karatmeter Testing';
  const amenity2 = storeSettings?.storeAmenity2 || 'Valet Parking Available';
  const amenity3 = storeSettings?.storeAmenity3 || 'Private Bridal Lounge';
  const amenity4 = storeSettings?.storeAmenity4 || 'Zero-Loss Gold Exchange';
  const vipButtonText = storeSettings?.storeVipVisitText || 'Book VIP Visit';
  const mapsUrl = storeSettings?.storeGoogleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(storeName + ' ' + storeAddress)}`;
  const embedUrl = storeSettings?.storeGoogleMapsEmbed || '';

  const openMapDirections = () => {
    window.open(mapsUrl, '_blank');
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(`Namaste ${storeName}! I want to book a VIP showroom visit to view Bridal Jewellery and Live Bullion rates.`);
    window.open(`https://wa.me/${cleanPhone.replace('+', '')}?text=${text}`, '_blank');
  };

  return (
    <section 
      id="store-location-section"
      className="store-locator-section" 
      style={{
        padding: '2.5rem 1rem 3rem',
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0'
      }}
    >
      <div className="container" style={{ maxWidth: '1240px', margin: '0 auto' }}>
        
        {/* Section Heading */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            padding: '0.25rem 0.75rem',
            borderRadius: '20px',
            fontSize: '0.74rem',
            fontWeight: 800,
            color: '#1d4ed8',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.4rem'
          }}>
            <MapPin size={13} />
            <span>{badgeText}</span>
          </div>
          <h2 style={{
            margin: '0 0 0.4rem',
            fontSize: 'clamp(1.3rem, 2.4vw, 1.85rem)',
            fontWeight: 800,
            color: '#1e293b',
            fontFamily: "'Playfair Display', serif, Georgia"
          }}>
            {headingText}
          </h2>
          <p style={{
            margin: 0,
            fontSize: '0.86rem',
            color: '#64748b',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            {subtitleText}
          </p>
        </div>

        {/* Showroom Details & Interactive Map Mockup Card */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '1.5rem',
          background: '#ffffff',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden'
        }}>
          {/* Left Column: Address, Timings, Amenities */}
          <div style={{ padding: '2rem 1.8rem' }}>
            <h3 style={{
              margin: '0 0 1rem',
              fontSize: '1.25rem',
              fontWeight: 800,
              color: '#0f172a',
              fontFamily: "'Playfair Display', serif, Georgia"
            }}>
              {storeName}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem', fontSize: '0.86rem', color: '#334155' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <MapPin size={18} color="#2563eb" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong style={{ display: 'block', color: '#0f172a', marginBottom: '0.2rem' }}>Flagship Address:</strong>
                  <span>{storeAddress}</span>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <Clock size={18} color="#2563eb" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong style={{ display: 'block', color: '#0f172a', marginBottom: '0.2rem' }}>Showroom Timings:</strong>
                  <span>{storeTimings}</span>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <Phone size={18} color="#2563eb" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong style={{ display: 'block', color: '#0f172a', marginBottom: '0.2rem' }}>Direct Concierge:</strong>
                  <a href={`tel:${cleanPhone}`} style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 700 }}>
                    {storePhone}
                  </a>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <Mail size={18} color="#2563eb" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong style={{ display: 'block', color: '#0f172a', marginBottom: '0.2rem' }}>Email Desk:</strong>
                  <span>{storeEmail}</span>
                </div>
              </div>
            </div>

            {/* Showroom Amenities Badges */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '0.6rem',
              padding: '1rem 0',
              borderTop: '1px solid #f1f5f9',
              borderBottom: '1px solid #f1f5f9',
              marginBottom: '1.5rem',
              fontSize: '0.76rem',
              color: '#475569'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <ShieldCheck size={15} color="#16a34a" />
                <span>{amenity1}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Car size={15} color="#2563eb" />
                <span>{amenity2}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Sparkles size={15} color="#d97706" />
                <span>{amenity3}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Award size={15} color="#7c3aed" />
                <span>{amenity4}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
              <button
                onClick={openMapDirections}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.45rem',
                  background: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.7rem 1.25rem',
                  fontSize: '0.84rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 10px rgba(37, 99, 235, 0.25)'
                }}
              >
                <Navigation size={15} />
                <span>Get Directions (Google Maps)</span>
              </button>

              <button
                onClick={handleWhatsApp}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.45rem',
                  background: '#25D366',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.7rem 1.15rem',
                  fontSize: '0.84rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 10px rgba(37, 211, 102, 0.25)'
                }}
              >
                <MessageCircle size={15} />
                <span>{vipButtonText}</span>
              </button>
            </div>
          </div>

          {/* Right Column: Visual Map Card with Location Pin */}
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            position: 'relative',
            minHeight: '280px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            color: '#ffffff',
            textAlign: 'center'
          }}>
            {/* Background Grid Pattern */}
            <div style={{
              position: 'absolute',
              inset: 0,
              opacity: 0.12,
              backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
              backgroundSize: '16px 16px'
            }} />

            <div style={{ position: 'relative', zIndex: 2, maxWidth: '340px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'rgba(37, 99, 235, 0.25)',
                border: '2px solid #3b82f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.2rem',
                boxShadow: '0 0 24px rgba(59, 130, 246, 0.5)'
              }}>
                <MapPin size={28} color="#60a5fa" />
              </div>

              <h4 style={{ margin: '0 0 0.4rem', fontSize: '1.15rem', fontWeight: 800 }}>
                {storeName}
              </h4>
              <p style={{ margin: '0 0 1.2rem', fontSize: '0.8rem', color: '#94a3b8', lineHeight: 1.4 }}>
                {storeAddress}
              </p>

              <button
                onClick={openMapDirections}
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  padding: '0.6rem 1.2rem',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  backdropFilter: 'blur(4px)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                <Navigation size={13} />
                <span>Open in Google Maps</span>
              </button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
